export default function WhoWeServe() {
  return (
    <section id="industries" className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-accent-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Industries We Serve
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-primary-950 max-w-3xl mx-auto leading-tight">
              Four Client Types.
              <span className="italic text-accent-600"> One Standard </span>
              of Excellence.
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Restaurants */}
            <div className="relative rounded-2xl overflow-hidden bg-primary-950">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Busy%20modern%20restaurant%20dining%20room%20during%20service%20with%20warm%20ambient%20lighting%2C%20chefs%20in%20open%20kitchen%20background%2C%20guests%20enjoying%20meals%2C%20cozy%20upscale%20atmosphere%2C%20professional%20food%20photography%20style%2C%20soft%20bokeh&width=800&height=600&seq=ind-restaurant-01&orientation=landscape"
                  alt="Restaurant interior during service"
                  className="w-full h-full object-cover object-top opacity-40"
                />
              </div>
              <div className="relative p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-accent-500/20 text-accent-300 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                  <i className="ri-restaurant-2-line" />
                  Food & Hospitality
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-bold mb-4">
                  Restaurants, Cafes, Bars & Catering
                </h3>
                <p className="text-background-300 text-sm md:text-base leading-relaxed mb-6">
                  From food trucks to full-service dining, we understand the unique financial
                  pressures of hospitality: razor-thin margins, volatile labor costs, complex
                  tipped wages, and perishable inventory.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Prime cost tracking (food + labor as % of sales)',
                    'Tipped payroll compliance & FICA tip credits',
                    'POS integration (Toast, Square, Clover)',
                    'Menu engineering & pricing analysis',
                    'Virginia ABC & meals tax compliance',
                    'Vendor cost variance & inventory turnover',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-accent-500/30 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent-300 text-xs" />
                      </div>
                      <span className="text-background-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-700/50 pt-6">
                  <p className="text-background-400 text-xs uppercase tracking-wider font-semibold mb-2">
                    Ideal Client
                  </p>
                  <p className="text-background-200 text-sm">
                    Independent restaurants, cafes, bars, and catering companies with 5–75
                    employees in the Richmond area seeking proactive financial partners who
                    understand hospitality.
                  </p>
                </div>
              </div>
            </div>

            {/* Trades */}
            <div className="relative rounded-2xl overflow-hidden bg-primary-950">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Skilled%20trades%20contractor%20at%20residential%20job%20site%20reviewing%20plans%20on%20clipboard%2C%20professional%20electrician%20or%20plumber%20working%20with%20tools%2C%20clean%20organized%20work%20van%20in%20background%2C%20warm%20daylight%2C%20professional%20trades%20photography%20style&width=800&height=600&seq=ind-trades-01&orientation=landscape"
                  alt="Skilled trades contractor at job site"
                  className="w-full h-full object-cover object-top opacity-40"
                />
              </div>
              <div className="relative p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-secondary-500/20 text-secondary-300 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                  <i className="ri-tools-line" />
                  Skilled Trades
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-bold mb-4">
                  Electricians, Plumbers, HVAC & General Contractors
                </h3>
                <p className="text-background-300 text-sm md:text-base leading-relaxed mb-6">
                  Trades businesses run on job-level profitability, not monthly averages.
                  We track every project, change order, and material cost so you always know
                  where you stand.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Per-project job costing & overhead allocation',
                    'Change order tracking & billing reconciliation',
                    'Retainage accounting & A/R aging by project',
                    'Material markup analysis & vendor comparisons',
                    'Cash flow forecasting for seasonal swings',
                    'WIP (Work in Progress) & percentage completion',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary-500/30 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-secondary-300 text-xs" />
                      </div>
                      <span className="text-background-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-700/50 pt-6">
                  <p className="text-background-400 text-xs uppercase tracking-wider font-semibold mb-2">
                    Ideal Client
                  </p>
                  <p className="text-background-200 text-sm">
                    Owner-operated electrical, plumbing, HVAC, and general contracting
                    businesses with 2–25 employees across Richmond, Henrico, Chesterfield,
                    and surrounding counties.
                  </p>
                </div>
              </div>
            </div>

            {/* Small Businesses */}
            <div className="relative rounded-2xl overflow-hidden bg-primary-950">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Small%20business%20owner%20reviewing%20financial%20reports%20on%20a%20laptop%20at%20a%20tidy%20retail%20shop%20counter%2C%20warm%20natural%20daylight%20through%20the%20window%2C%20organized%20workspace%20with%20receipts%20and%20coffee%2C%20professional%20lifestyle%20photography%20style%2C%20soft%20neutral%20tones&width=800&height=600&seq=ind-smallbiz-01&orientation=landscape"
                  alt="Small business owner reviewing financial reports"
                  className="w-full h-full object-cover object-top opacity-40"
                />
              </div>
              <div className="relative p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-accent-500/20 text-accent-300 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                  <i className="ri-store-2-line" />
                  Small Business
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-bold mb-4">
                  Owner-Operated Small Businesses
                </h3>
                <p className="text-background-300 text-sm md:text-base leading-relaxed mb-6">
                  From retail shops to professional services, we keep your books clean and
                  compliant so you can make confident decisions about pricing, hiring, and
                  growth — without the cost of a full-time accountant.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Monthly bookkeeping & bank reconciliation',
                    'Payroll processing & compliance',
                    'Sales tax & regulatory filing',
                    'Custom reporting & forecasting',
                    'Software setup & integrations',
                    'Year-end cleanup & CPA coordination',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-accent-500/30 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent-300 text-xs" />
                      </div>
                      <span className="text-background-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-700/50 pt-6">
                  <p className="text-background-400 text-xs uppercase tracking-wider font-semibold mb-2">
                    Ideal Client
                  </p>
                  <p className="text-background-200 text-sm">
                    Local owner-operated small businesses seeking dependable monthly
                    bookkeeping, compliance support, and clear reporting in the Richmond
                    area.
                  </p>
                </div>
              </div>
            </div>

            {/* Nonprofits */}
            <div className="relative rounded-2xl overflow-hidden bg-primary-950">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Nonprofit%20team%20of%20volunteers%20collaborating%20around%20a%20table%20with%20financial%20documents%20and%20a%20laptop%20in%20a%20bright%20community%20office%2C%20warm%20welcoming%20atmosphere%2C%20organized%20paperwork%2C%20professional%20documentary%20photography%20style%2C%20soft%20natural%20lighting&width=800&height=600&seq=ind-nonprofit-01&orientation=landscape"
                  alt="Nonprofit team collaborating over financial documents"
                  className="w-full h-full object-cover object-top opacity-40"
                />
              </div>
              <div className="relative p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-secondary-500/20 text-secondary-300 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                  <i className="ri-heart-3-line" />
                  Nonprofit
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-bold mb-4">
                  Nonprofits & Community Organizations
                </h3>
                <p className="text-background-300 text-sm md:text-base leading-relaxed mb-6">
                  Mission-driven organizations need accurate, transparent books. We handle
                  the monthly detail and deliver clear reporting your board and donors can
                  trust.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Monthly bookkeeping & fund tracking',
                    'Payroll processing & compliance',
                    'Sales tax & regulatory filing',
                    'Custom reporting & forecasting',
                    'Software setup & integrations',
                    'Year-end cleanup & CPA coordination',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary-500/30 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-secondary-300 text-xs" />
                      </div>
                      <span className="text-background-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-background-700/50 pt-6">
                  <p className="text-background-400 text-xs uppercase tracking-wider font-semibold mb-2">
                    Ideal Client
                  </p>
                  <p className="text-background-200 text-sm">
                    Nonprofits, charitable organizations, and community groups that need
                    dependable bookkeeping, compliance support, and board-ready reporting
                    in the Richmond area.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}